import { NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
  SquaresFour, PlugsConnected, GitBranch, ListChecks,
  Question, Scroll, Gear, SignOut, Database, MagnifyingGlass, BellSimple, Cube, UsersThree,
  CreditCard, ShieldStar,
} from "@phosphor-icons/react";

const NAV = [
  { to: "/", label: "Dashboard", icon: SquaresFour, end: true },
  { to: "/connections", label: "Connections", icon: PlugsConnected },
  { to: "/connector-studio", label: "Connector Studio", icon: Cube },
  { to: "/projects", label: "Migration Projects", icon: GitBranch },
  { to: "/jobs", label: "Migration Jobs", icon: ListChecks },
  { to: "/records", label: "Records Explorer", icon: MagnifyingGlass },
  { to: "/notifications", label: "Notifications", icon: BellSimple },
  { to: "/audit", label: "Audit Logs", icon: Scroll },
  { to: "/team", label: "Team", icon: UsersThree, roles: ["owner", "admin"] },
  { to: "/billing", label: "Billing", icon: CreditCard, roles: ["owner", "admin"] },
  { to: "/control", label: "Control Portal", icon: ShieldStar, superAdmin: true },
  { to: "/help", label: "Help", icon: Question },
  { to: "/settings", label: "Settings", icon: Gear },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex bg-stone-50">
      {/* Floating Velvet-Indigo sidebar */}
      <aside className="w-64 shrink-0 fixed top-0 left-0 h-screen p-3 z-40">
        <div className="h-full flex flex-col brand-ink text-white rounded-3xl shadow-float overflow-hidden border border-white/5">
          <div className="h-16 flex items-center gap-2.5 px-5 border-b border-white/10">
            <span className="w-8 h-8 rounded-xl bg-[#C2410C] flex items-center justify-center shadow-glow">
              <Database size={18} weight="fill" className="text-white" />
            </span>
            <span className="font-head font-semibold text-[15px] tracking-tight">Inspiro Interface</span>
          </div>
          <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
            {NAV.filter((n) => (!n.roles || n.roles.includes(user?.role)) && (!n.superAdmin || user?.is_super_admin)).map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all duration-200 ${
                    isActive
                      ? "bg-[#C2410C] text-white font-medium shadow-glow"
                      : "text-zinc-400 hover:bg-white/[0.07] hover:text-white"
                  }`
                }
              >
                <n.icon size={18} weight="regular" />
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="p-4 border-t border-white/10">
            <div className="text-[10px] text-zinc-500 uppercase tracking-[0.18em] mb-1">Organization</div>
            <div className="text-sm font-medium truncate">{user?.org_name}</div>
            <div className="text-xs text-zinc-500 truncate mb-3">{user?.email}</div>
            <button
              data-testid="logout-btn"
              onClick={() => { logout(); navigate("/login"); }}
              className="flex items-center gap-2 text-xs text-zinc-400 hover:text-white transition-colors duration-200"
            >
              <SignOut size={15} /> Sign out
            </button>
          </div>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 ml-64 min-h-screen"><ImpersonationBanner />{children}</main>
    </div>
  );
}

function ImpersonationBanner() {
  const { user } = useAuth();
  const imp = user?.impersonation;
  if (!imp?.active) return null;
  const exit = async () => {
    try { await api.post("/admin/stop-impersonation"); } catch (e) {}
    window.location.href = "/control";
  };
  return (
    <div data-testid="impersonation-banner"
      className="sticky top-0 z-30 bg-[#C2410C] text-white px-6 py-2.5 flex items-center justify-between text-sm">
      <span className="flex items-center gap-2">
        <ShieldStar size={16} weight="fill" />
        Viewing <strong>{imp.org_name}</strong> as Super Admin{imp.by ? ` · ${imp.by}` : ""}
      </span>
      <button data-testid="impersonation-exit" onClick={exit}
        className="rounded-full bg-white/20 hover:bg-white/30 px-4 py-1 font-medium transition-colors">
        Exit to Control Portal
      </button>
    </div>
  );
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="h-16 bg-white/80 backdrop-blur-xl border-b border-stone-200 flex items-center justify-between px-8 sticky top-0 z-20">
      <div>
        <h1 className="font-head font-semibold text-xl tracking-tight text-stone-900 leading-none">{title}</h1>
        {subtitle && <p className="text-xs text-stone-500 mt-1">{subtitle}</p>}
      </div>
      <div className="flex items-center gap-3">
        {actions}
        <NotificationBell />
      </div>
    </div>
  );
}

function NotificationBell() {
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    const load = () => api.get("/notifications").then((r) => setUnread(r.data.unread || 0)).catch(() => {});
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, []);

  return (
    <button data-testid="header-notification-bell" onClick={() => navigate("/notifications")}
      className="relative p-2 rounded-xl text-stone-500 hover:text-[#C2410C] hover:bg-stone-100 transition-colors duration-200">
      <BellSimple size={20} weight={unread > 0 ? "fill" : "regular"} />
      {unread > 0 && (
        <span data-testid="notification-unread-badge"
          className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-rose-600 text-white text-[10px] font-bold leading-none">
          {unread > 9 ? "9+" : unread}
        </span>
      )}
    </button>
  );
}
